/**
 * Turning a finished order back into a draft.
 *
 * Shared by the two places that offer it — the finished order's own screen and the home screen's
 * recents — because the resolution rules are the interesting part and having them in two places is
 * how the two start disagreeing about whether a sold car can be repeated.
 *
 * Everything the historical order depended on is resolved against **today** before the wizard is
 * opened: the cars against the current garage, the card against the current list. Jumping straight
 * to the summary and finding out there would put the refusal on the button that spends money.
 */
import { fetchCars } from '@/entities/car/api';
import { fetchOrderDetail, type OrderDetail } from '@/entities/order/detail';
import { saveOrderDraft } from '@/entities/order/draftStorage';
import { buildRepeatDraft, withRepeatHours } from '@/entities/order/model/repeat';
import { fetchServiceArea } from '@/entities/order/serviceArea';
import { isCarStillInOrder } from '@/entities/order/model/tracking';
import { fetchPaymentMethods, isExpired, paymentMethodLabel } from '@/entities/payment-method/api';
import { carLine } from './CarPickerScreen';

/**
 * Writes the draft and reports whether a car survived.
 *
 * `false` means the wizard has to open on the picker: a draft with nothing to fuel cannot get past
 * its own first gate, and dropping somebody on step two of a form they cannot finish is worse than
 * asking the question again.
 */
export async function prepareRepeatDraft(order: OrderDetail): Promise<boolean> {
  // Зона той же точки — ради часов работы: ночью повтор «Ближайшей» упёрся бы в отказ на кнопке
  // оплаты. Сбой проверки повтор не ломает — см. `withRepeatHours`.
  const [garage, methods, area] = await Promise.all([
    fetchCars(),
    fetchPaymentMethods(),
    fetchServiceArea(order.latitude, order.longitude).catch(() => null),
  ]);

  const primary = garage.find((c) => c.id === order.car_id) ?? null;

  // Only the cars that were actually on the выезд, and only those still in the garage. A car
  // dropped mid-order was not part of what happened, and repeating it would be inventing.
  const extras = order.cars
    .filter((c) => isCarStillInOrder(c.status) && c.car_id !== order.car_id)
    .map((c) => garage.find((g) => g.id === c.car_id))
    .filter((c) => c !== undefined)
    .map((c) => ({
      carId: c.id,
      label: carLine(c),
      liters: '',
      fuelTypeCode: c.fuel_type_code,
      tankVolumeLiters: c.tank_volume_liters,
      brand: c.brand,
    }));

  // Expired counts as gone: the card is in the list but the hold will be declined, and the summary
  // is the wrong place to discover that. У заказа по СБП карты нет вовсе — `null`, и повтор так же
  // приземляется на шаг оплаты, где способ выбирают заново.
  const card =
    order.payment_method_id === null
      ? null
      : (methods.find((m) => m.id === order.payment_method_id && !isExpired(m)) ?? null);

  saveOrderDraft(
    withRepeatHours(
      buildRepeatDraft(
        {
          addressText: order.address_text,
          latitude: order.latitude,
          longitude: order.longitude,
          operatorComment: order.operator_comment,
          ownerPresent: order.owner_present,
          fuelAmountLiters: order.fuel_amount_liters,
          requestedFuelLiters: order.requested_fuel_liters,
        },
        primary === null
          ? null
          : {
              id: primary.id,
              label: carLine(primary),
              brand: primary.brand,
              fuelTypeCode: primary.fuel_type_code,
              tankVolumeLiters: primary.tank_volume_liters,
            },
        card === null ? null : { id: card.id, label: paymentMethodLabel(card) },
        extras,
      ),
      area,
    ),
  );

  return primary !== null;
}

/**
 * The same, from a list row that only knows an id.
 *
 * The detail is fetched first rather than repeating from the summary shape: a history row carries
 * neither the extra cars nor the payment method, and a repeat built from it would quietly drop
 * both — turning a three-car выезд into a one-car one without saying so.
 */
export async function prepareRepeatDraftById(orderId: string): Promise<boolean> {
  const { order } = await fetchOrderDetail(orderId);
  return prepareRepeatDraft(order);
}
